import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { FormControlLabel, Grid, Typography, Box } from '@mui/material'
import { useFormikContext } from 'formik'
import React, { useEffect } from 'react'
import { makeStyles } from 'tss-react/mui'
import { Stack } from '@mui/system'

import { useCountryOptions } from '@e3dc-react/shell/Hooks/useCountryOptions'
import SeparatorLine from 'src/Elements/Separator/SeparatorLine'
import Select from 'src/Libs/inputs/formik/Select'
import ConditionalTooltipCheckbox from 'src/Libs/inputs/formik/ConditionalTooltipCheckbox'
import { TextField } from 'src/Libs/inputs/formik'
import { CompanyForm } from '../models/company.model'
import { useInstallationsAttachedToCompany } from '../../installations/hooks/use-installations-attached-to-company'
import CompanyViewFormActionButtons from './CompanyViewFormActionButtons'
import { getPartnerNumberWithSpaces } from '../functions'
import { ButtonWithLoading } from '@e3dc-react/shell/Elements/Buttons'
import { useShopCountryAccess } from 'src/Libs/shop/hooks/use-shop-country-access'
import CompanyAutocompleteWithCreation from './CompanyAutocompleteWithCreation'
import { NdaDownloadButton } from 'src/Libs/nda'

const useStyles = makeStyles()(theme => ({
  form: {
    marginTop: theme.spacing(1),
  },
  checkboxRowContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  city: {
    [theme.breakpoints.up('md')]: {
      /** overrides grid container spacing. */
      paddingLeft: `${theme.spacing(2)} !important`,
    },
  },
  partnerNumberContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
}))

interface Props {
  companyId?: number
}

/**
 * Company Form to edit Company details
 */
const CompanyViewForm: React.FC<Props> = ({ companyId }) => {
  const { classes } = useStyles()
  const { t } = useCustomTranslation()
  const { handleSubmit, setValues, initialValues, values, setFieldValue, isValidating } = useFormikContext<CompanyForm>()
  const { allCountries } = useCountryOptions()
  const [attachedInstallations, attachedInstallationsLoading] = useInstallationsAttachedToCompany(companyId?.toString())
  const isDisabledRemoving = !!attachedInstallations.length || attachedInstallationsLoading
  const { checkIfCompanyHasShopAccess } = useShopCountryAccess()
  const isShopPermissionDisabled = !values.sapCustomerId || !checkIfCompanyHasShopAccess(values.country)
  const partnerNumber = values.partnerNumber

  const checkShopAccessByCountry = (): string => {
    if (!checkIfCompanyHasShopAccess(values.country)) {
      return t('service-dashboard.company.access-not-allowed')
    }
    return ''
  }

  const reasonToDisableShopPermission = !values.sapCustomerId ? t('service-dashboard.company.sap-id-required') : checkShopAccessByCountry()

  const handleShopPermissionChange = (): void => {
    setFieldValue('shopPermission', !values.shopPermission)
  }

  useEffect(() => {
    setValues(initialValues)
  }, [companyId, initialValues, setValues])

  return (
    <form className={classes.form} onSubmit={handleSubmit}>
      <Grid container>
        <Grid item container spacing={4} xs={12} md={6} alignContent={'flex-start'}>
          <Grid item xs={12}>
            <CompanyAutocompleteWithCreation name={'name'} maxDisplayedResultCount={3} withTrim />
          </Grid>
          <Grid item xs={12}>
            <TextField name={'address'} label={t('common.address')} required withTrim />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField name={'zipCode'} label={t('common.zip')} required withTrim delayInMs={0} />
          </Grid>
          <Grid item xs={12} md={9} className={classes.city}>
            <TextField name={'city'} label={t('common.city')} required withTrim />
          </Grid>
          <Grid item xs={12}>
            <Select name={'country'} label={t('common.country')} options={allCountries} isSearchable required />
          </Grid>
          <Grid item xs={12} className={classes.checkboxRowContainer}>
            <FormControlLabel
              control={
                <ConditionalTooltipCheckbox
                  checked={values.shopPermission}
                  onChange={handleShopPermissionChange}
                  disabled={isShopPermissionDisabled}
                  disableReason={reasonToDisableShopPermission}
                />
              }
              label={t('service-dashboard.company.shop-permission')}
            />
          </Grid>
          <Grid item xs={12} />
        </Grid>
        <Grid item container spacing={4} xs={12} md={6} alignContent={'flex-start'}>
          <Grid item xs={12}>
            <TextField name={'phone'} label={t('common.phone')} required withTrim />
          </Grid>
          <Grid item xs={12}>
            <TextField name={'fax'} label={t('common.fax')} withTrim />
          </Grid>
          <Grid item xs={12}>
            <TextField name={'email'} label={t('common.email')} required withTrim />
          </Grid>
          <Grid item xs={12}>
            <TextField name={'sapCustomerId'} label={t('common.sapCustomerId')} delayInMs={500} withTrim />
          </Grid>
          {!!partnerNumber?.length && (
            <Grid item xs={12}>
              <Box className={classes.partnerNumberContainer}>
                <Typography variant="caption">{t('common.partner-number')}</Typography>
                <Typography variant="body2">{getPartnerNumberWithSpaces(partnerNumber)}</Typography>
              </Box>
            </Grid>
          )}
          {!!companyId && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                {values.ndaSubmit ? (
                  <NdaDownloadButton companyId={companyId} companyName={initialValues.name} withMenu />
                ) : (
                  <Typography variant={'subtitle2'}>{t('nda.download-button.not-available')}</Typography>
                )}
              </Box>
            </Grid>
          )}
        </Grid>
        <Grid item xs={12}>
          <SeparatorLine />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ justifyContent: companyId ? 'space-between' : 'flex-end' }}>
            {companyId && (
              <CompanyViewFormActionButtons
                companyId={companyId}
                companyName={values.name}
                isDisabledRemoving={isDisabledRemoving}
                partnerNumber={values.partnerNumber}
              />
            )}
            <ButtonWithLoading type="submit" variant="contained" size="large" color="primary" isLoading={isValidating} label={t('common.save')} />
          </Stack>
        </Grid>
      </Grid>
    </form>
  )
}

export default CompanyViewForm
